import { GetApiPayloadByUrl, GetApiResponseByUrl } from '@crossbeam/openapi';
import { Nullable } from '@crossbeam/types';

import { crossbeamApi } from '@/api';
import { ACCOUNT_PRODUCT_DISPLAY_NAME, MDM_TYPES } from '@/constants/mdm';
import type { DataSourceType } from '@/types/feeds';
import { Population } from '@/types/populations';
import { Source, SourceField } from '@/types/sources';
import { sortObjectsByMdmType } from '@/utils';

export const FILE_UPLOAD_DATA_SOURCE_TYPE = 'file_upload';
export const GOOGLE_SHEETS_DATA_SOURCE_TYPE = 'google_sheets';
export const PIPEDRIVE_DATA_SOURCE_TYPE = 'pipedrive';
export const HUBSPOT_DATA_SOURCE_TYPE = 'hubspot';
export const HS3_DATA_SOURCE_TYPE = 'hubspot_v3';
export const SALESFORCE_DATA_SOURCE_TYPE = 'salesforce';
export const SNOWFLAKE_DATA_SOURCE_TYPE = 'snowflake';
export const SNOWFLAKE_INTEGRATION_PREFIX = 'snowflake';
export const MD_DATA_SOURCE_TYPE = 'microsoft_dynamics';
export const DATABRICKS_DATA_SOURCE_TYPE = 'databricks';
export const DELL_SPIL_DATA_SOURCE_TYPE = 'dell_spil';
export const EXTERNAL_FILE_DATA_SOURCE_TYPE = 'external_file_data';
export const AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE = 'aws_partner_central';
export const OPEN_DATA_PARTNER_DATA_SOURCE_TYPE = 'trace';

export const SALESFORCE_INTEGRATION_FRIENDLY_NAME = 'Salesforce';
export const SNOWFLAKE_INTEGRATION_FRIENDLY_NAME = 'Snowflake';
export const DATABRICKS_INTEGRATION_FRIENDLY_NAME = 'Databricks';

/**
 * @returns { Record<TDataSourceType, string> }
 */
export const DataSourceFriendlyName: Record<string, string> = {
  [SALESFORCE_DATA_SOURCE_TYPE]: 'Salesforce',
  [HS3_DATA_SOURCE_TYPE]: 'HubSpot',
  [SNOWFLAKE_DATA_SOURCE_TYPE]: 'Snowflake',
  [PIPEDRIVE_DATA_SOURCE_TYPE]: 'Pipedrive',
  [MD_DATA_SOURCE_TYPE]: 'Microsoft Dynamics',
  [GOOGLE_SHEETS_DATA_SOURCE_TYPE]: 'Google Sheets',
  [FILE_UPLOAD_DATA_SOURCE_TYPE]: 'CSV',
  [DATABRICKS_DATA_SOURCE_TYPE]: 'Databricks',
  [DELL_SPIL_DATA_SOURCE_TYPE]: 'Dell Spil',
  [AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE]: 'AWS Partner Central',
};

export const DataSourceSvgIcon: Record<string, string> = {
  [GOOGLE_SHEETS_DATA_SOURCE_TYPE]: 'googleSheetsIcon',
  [FILE_UPLOAD_DATA_SOURCE_TYPE]: 'fileUploadIcon',
  [DATABRICKS_DATA_SOURCE_TYPE]: 'databricksIcon',
};

export const DataSourceDomain: Record<string, string> = {
  crossbeam: 'crossbeam.com',
  [SALESFORCE_DATA_SOURCE_TYPE]: 'salesforce.com',
  [HS3_DATA_SOURCE_TYPE]: 'hubspot.com',
  [MD_DATA_SOURCE_TYPE]: 'dynamics.microsoft.com',
  [PIPEDRIVE_DATA_SOURCE_TYPE]: 'pipedrive.com',
  [SNOWFLAKE_DATA_SOURCE_TYPE]: 'snowflake.com',
  [DATABRICKS_DATA_SOURCE_TYPE]: 'databricks.com',
  [GOOGLE_SHEETS_DATA_SOURCE_TYPE]: 'sheets.google.com',
  [DELL_SPIL_DATA_SOURCE_TYPE]: 'dell.com',
  [AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE]: 'aws.amazon.com',
};

/**
 * Friendly labels for individual source tables whose raw names are not
 * user-presentable (e.g. Dell SPIL virtual objects). Keyed by Source.table.
 */
const SourceTableFriendlyName: Record<string, string> = {
  cb_accountprograms: 'Account Programs',
  cb_accountteams: 'Account Teams',
};

/**
 * Display label for a source table: the friendly name if one is mapped,
 * otherwise `fallback` (defaults to the raw table). Use this everywhere a
 * source-grouped field picker renders a source/object header so new surfaces
 * pick up friendly names without re-implementing the lookup.
 */
export function getSourceTableLabel(
  table: string,
  fallback: string = table,
): string {
  return SourceTableFriendlyName[table] ?? fallback;
}

/* Friendly label for a source node, given the full source. AWS Partner Central
   is stored as an `account`-typed source (its table is "accounts", shared with
   other integrations like Databricks), but is surfaced to users as "AWS Lead".
   We detect it via the schema prefix, which muncher generates as
   `<integration_type>_<suffix>`. */
export function getSourceLabel(source: {
  table: string;
  schema?: string | null;
  mdm_type?: string | null;
}): string {
  if (source.schema?.startsWith(AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE)) {
    return 'AWS Lead';
  }
  const tableLabel = getSourceTableLabel(source.table);
  if (source.mdm_type === MDM_TYPES.ACCOUNT_PRODUCT)
    return `${tableLabel} (${ACCOUNT_PRODUCT_DISPLAY_NAME})`;
  return tableLabel;
}

/* The AWS Partner Central offline-partner org is created by the backend with the
   friendly name as its org name (the same signal useAwsPartnerCentral keys off).
   Consumption-only, so surfaces that would prompt CSV upload must skip it. */
export function isAwsPartnerCentralPartnerOrg(
  partner?: { name?: string | null } | null,
): boolean {
  return (
    partner?.name ===
    DataSourceFriendlyName[AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE]
  );
}

export const MAX_SALESFORCE_CONNECTIONS = 3;
export const RECORD_LIMIT_THRESHOLD = 1000;
export const SYNC_FEED_INTERVAL = 10000;
export const DELETE_FEED_INTERVAL = 10000;
export const MAX_FIELDS = 125;

export const DATA_TEMPLATES_CRMS = [
  SALESFORCE_DATA_SOURCE_TYPE,
  MD_DATA_SOURCE_TYPE,
  HS3_DATA_SOURCE_TYPE,
  PIPEDRIVE_DATA_SOURCE_TYPE,
];
// We have Hubspot feeds converted into HS3
// Those sources don't have 'hubspot_v3_...' schema but have 'hubspot_...' schema instead.
export const DEAL_CAPABLE_CRMS = [
  SALESFORCE_DATA_SOURCE_TYPE,
  MD_DATA_SOURCE_TYPE,
  HUBSPOT_DATA_SOURCE_TYPE,
  SNOWFLAKE_DATA_SOURCE_TYPE,
];

/* Possible statuses for a source */
export const SOURCE_STATUS_MAP = {
  process: ['PROCESSING', 'SCHEDULED_TO_PROCESS'],
  done: ['PROCESSED'],
  error: ['ERROR', 'PROCESSING_FAILED', 'COLUMN_ERRORS'],
  warning: ['DUPLICATE_ROWS'],
  deleting: ['DELETING'],
  missing: ['UNAUTHORIZED'],
} as const;

type SourceStatusMap = typeof SOURCE_STATUS_MAP;
export type SourceStatusCategory = keyof SourceStatusMap;
export type AllSourceStatus = SourceStatusMap[SourceStatusCategory][number];
export type SourceDoneStatus = SourceStatusMap['done'][number];
export type SourceErrorStatus = SourceStatusMap['error'][number];

/* The possible statuses for a feed */
export const REQUIRED_FEED_STATUS = 'REQUIRE_SOURCES_SELECTION';
export const PENDING_FEED_STATUS = 'OAUTH_COMPLETE';
export const ACTIVE_FEED_STATUS = 'CONFIGURED';
export const CONNECTION_CHECK_ERROR_STATUS = 'CONN_CHECK_FAILED';
export const RECORD_LIMIT_EXCEEDED_STATUS = 'RECORD_LIMIT_EXCEEDED';
export const SOURCE_FAILED_STATUS = 'SOURCE_FAILED';
export const DELETING_FEED_STATUS = 'DELETING';
export const DELETION_FAILED_FEED_STATUS = 'DELETION_FAILED';

export const COMPLETED_STATUSES = [PENDING_FEED_STATUS, ACTIVE_FEED_STATUS];
export const WARNING_STATUSES = [SOURCE_FAILED_STATUS];

export const LOOKUP_FIELD = 'lookup-field';
export const SOURCE_FIELD = 'source-field';
export const SOURCE = 'source';

export const ALL_FIELDS = 'all_fields';
export const SELECTED_FIELDS = 'selected_fields';

export const LOOKUP_PARENT = 'lookup_parent';
export const CONTAINS_LOOKUPS = 'contains_lookups';
export const CONTAINS_COPIED = 'contains_copied';

export const SYNCED = 'synced';
export const UNSYNCED = 'unsynced';
export const UNSYNCED_DISABLED = 'unsynced=disabled';
export const CANNOT_UNSYNC = 'cannot-unsync-field';
export const CANNOT_UNSYNC_PLURAL = 'cannot-unsync-fields';

const getLookupFieldString = (list: { copyingFieldName: string }[]) => {
  let str = '';
  list.forEach((item) => {
    str += ` ${item.copyingFieldName},`;
  });
  return str;
};

export const getIconTooltipText = (
  linkedSource: Nullable<SourceField>,
  messageType:
    | typeof LOOKUP_PARENT
    | typeof CONTAINS_COPIED
    | typeof CONTAINS_LOOKUPS,
) => {
  /*
  LOOKUP_PARENT is Source but source does not have a table property
  CONTAINS_LOOKUPS is null
  CONTAINS_COPIED is SourceField
  */

  switch (messageType) {
    case LOOKUP_PARENT:
      return `This field is linked to the ${linkedSource?.display_name} object`;
    case CONTAINS_COPIED:
    case CONTAINS_LOOKUPS:
      return 'Fields in this object include lookup fields';
  }
};

export type CopyingField = {
  copyingId: number;
  copyingSourceId: number;
  copyingRelId: string;
  copyingFieldName: string;
  initiallyChecked: boolean;
};
export type Message =
  | typeof SYNCED
  | typeof UNSYNCED
  | typeof UNSYNCED_DISABLED
  | typeof CANNOT_UNSYNC
  | typeof CANNOT_UNSYNC_PLURAL;
export const getFieldTooltipText = (
  fieldOrFields:
    | { display_name?: string | null }
    | CopyingField[]
    | null
    | undefined,
  source: { table?: string | null } | null,
  messageType: Message,
) => {
  const isArray = Array.isArray(fieldOrFields);
  switch (messageType) {
    case SYNCED:
      return !isArray && fieldOrFields
        ? `This field is synced to the ${fieldOrFields.display_name} field in the ${source?.table} object`
        : '';
    case UNSYNCED:
      // no tooltip for UNSYNCED
      return null;
    case UNSYNCED_DISABLED:
      return !isArray && fieldOrFields
        ? `Select ${fieldOrFields.display_name} to enable this field`
        : '';
    case CANNOT_UNSYNC:
      return isArray
        ? `Unsync lookup field ${fieldOrFields[0]?.copyingFieldName} in order to unsync this field.`
        : '';
    case CANNOT_UNSYNC_PLURAL:
      return isArray
        ? `Unsync lookup fields${getLookupFieldString(fieldOrFields)} in order to unsync this field.`
        : '';
  }
};

export const getKey = ({
  type,
  sourceId,
  fieldId = null,
  relId = null,
}: {
  type: string;
  sourceId: number;
  fieldId?: number | null;
  relId?: number | null;
}) => {
  let key;
  if (fieldId) {
    key = `${type}__${fieldId}__${sourceId}`;
  } else {
    key = `${type}__${sourceId}`;
  }
  if (relId) {
    key += `__${relId}`;
  }
  return key;
};

export function isInPopDefinition(pop: Population, fieldId: number) {
  if (!pop.source_filter_expressions) return false;
  return Object.values(pop.source_filter_expressions)
    .map((expr) => expr.filter_parts)
    .flat()
    .find((fp) => fp?.source_field_id === fieldId);
}

/* Returns the required columns for a given feed, per each source */
/* We may have to incorporate logic in here to account for fields from the template */
export async function getRequiredColumns(
  feed: GetApiResponseByUrl<'/v0.1/feeds'>['items'][number],
) {
  const dataSourceType = feed.integration.type;

  const { data, error } = await crossbeamApi.GET(
    '/v0.2/integrations/{integration_type}/required-columns',
    {
      params: {
        path: {
          integration_type: dataSourceType,
        },
      },
    },
  );
  if (error) throw error;

  return data?.reduce(
    (acc, { table, columns }) => ({ ...acc, [table]: columns }),
    {},
  );
}

type ColumnsByTableType = Record<string, string[]>;

/* The recommended set is identical for every org and feed, so it is memoized
per integration type for the session. The promise, not the resolved value, is
cached so concurrent callers share a single request. */
const recommendedColumnsCache = new Map<string, Promise<ColumnsByTableType>>();

async function requestRecommendedColumns(integrationType: string) {
  try {
    const { data, error, response } = await crossbeamApi.GET(
      '/v0.1/integrations/{integration_type}/recommended-columns',
      {
        params: {
          path: {
            integration_type: integrationType,
          },
        },
      },
    );
    /* An error response with an empty body leaves `error` undefined, which
    would otherwise be cached as an empty recommended set. */
    if (error || !response.ok)
      throw (
        error || new Error(`Recommended columns failed: ${response.status}`)
      );

    return (data ?? []).reduce<ColumnsByTableType>(
      (acc, { table, columns }) => ({ ...acc, [table]: columns }),
      {},
    );
  } catch (err) {
    recommendedColumnsCache.delete(integrationType);
    throw err;
  }
}

/* Returns the product's recommended columns for an integration type, per each
source. Only the CRMs in DATA_TEMPLATES_CRMS are served by the endpoint, so
anything else resolves empty instead of provoking a 404. */
export function getRecommendedColumns(integrationType: string) {
  if (!DATA_TEMPLATES_CRMS.includes(integrationType))
    return Promise.resolve<ColumnsByTableType>({});

  const cached = recommendedColumnsCache.get(integrationType);
  if (cached) return cached;

  const request = requestRecommendedColumns(integrationType);
  recommendedColumnsCache.set(integrationType, request);
  return request;
}

export function isValidSourceField(sourceField: SourceField) {
  return (
    !sourceField.column.startsWith('_sdc_') &&
    !sourceField.syncing_disabled_reason
  );
}

type SourceFieldsPayload = GetApiPayloadByUrl<
  '/v0.2/feeds/{id}/source-fields',
  'patch'
>;

type SyncPayloadType = {
  feedId: number;
  sources: NonNullable<SourceFieldsPayload['sources']>;
  fields: NonNullable<SourceFieldsPayload['fields']>;
};

export function buildSyncPayload({
  feedId,
  sources,
  checkedFieldIds,
}: {
  feedId: number;
  sources: Pick<
    Source,
    'id' | 'table' | 'fields' | 'mdm_type' | 'status' | 'sync_enabled'
  >[];
  checkedFieldIds: number[];
}): SyncPayloadType {
  const payload: SyncPayloadType = { feedId, sources: [], fields: [] };
  const syncingObjects = new Set<string>();
  /* Unauthorized sources render with every field unchecked, so leave them out
  rather than sending their fields and sync_enabled as switched off */
  const authorizedSources = sources.filter(
    (source) => source.status !== 'UNAUTHORIZED',
  );

  for (const source of authorizedSources) {
    for (const field of source.fields) {
      if (!field.id) continue;
      /* Fields of a disabled source render unchecked, so only count them as
      previously checked when the source syncs */
      const wasChecked =
        (source.sync_enabled ?? true) &&
        field.is_visible &&
        field.is_filterable;
      const isChecked = checkedFieldIds.includes(field.id);

      if (isChecked) syncingObjects.add(source.table);
      if (!wasChecked && isChecked) {
        payload.fields.push({
          id: field.id,
          is_filterable: true,
          is_visible: true,
        });
      } else if (wasChecked && !isChecked) {
        payload.fields.push({
          id: field.id,
          is_filterable: false,
          is_visible: false,
        });
      }
    }
  }

  const sortedSources = sortObjectsByMdmType(authorizedSources);
  for (const source of sortedSources) {
    if (source.id) {
      payload.sources.push({
        id: source.id,
        sync_enabled: syncingObjects.has(source.table),
      });
    }
  }

  return payload;
}

export async function patchSources({
  feedId,
  sources,
  fields,
}: {
  feedId: number;
  sources: SourceFieldsPayload['sources'];
  fields: SourceFieldsPayload['fields'];
}) {
  const { error } = await crossbeamApi.PATCH('/v0.2/feeds/{id}/source-fields', {
    params: {
      path: { id: feedId },
    },
    body: {
      sources,
      fields,
    },
  });
  if (error) {
    console.error(
      `Error while trying to update sources for feedId ${feedId}`,
      error,
    );
    throw error;
  }
}

export async function patchFeed(
  feedId: number,
  payload: GetApiPayloadByUrl<'/v0.1/feeds/{id}', 'patch'>,
  offlinePartnerOrgUuid?: string,
) {
  // AWS Partner Central feeds live on an offline-partner org; the backend
  // resolves it from offline_partner_org_uuid. The v0.1 patch route doesn't
  // declare the query param yet, hence the cast — drop it once the spec is
  // regenerated.
  const options = {
    params: {
      path: { id: feedId },
      ...(offlinePartnerOrgUuid && {
        query: { offline_partner_org_uuid: offlinePartnerOrgUuid },
      }),
    },
    body: payload,
  } as Parameters<typeof crossbeamApi.PATCH>[1];
  const { error } = await crossbeamApi.PATCH('/v0.1/feeds/{id}', options);
  if (error) throw error;
}

type SuggestFieldsResponseType = GetApiResponseByUrl<
  '/v0.1/feeds/{id}/suggest-fields',
  'post'
>;

export type SuggestFieldsPopulationType =
  SuggestFieldsResponseType['populations'][number];

export type SuggestedFieldType =
  SuggestFieldsPopulationType['suggested_fields'][number];

export async function getSuggestedFields(feedId: number) {
  const { data, error } = await crossbeamApi.POST(
    '/v0.1/feeds/{id}/suggest-fields',
    {
      params: {
        path: { id: feedId },
      },
    },
  );
  if (error) throw error;
  return data;
}

export function flattenSuggestedFields(
  populations: SuggestFieldsPopulationType[],
): SuggestedFieldType[] {
  const seen = new Set<string>();
  const result: SuggestedFieldType[] = [];
  for (const pop of populations) {
    for (const sf of pop.suggested_fields) {
      const key = `${sf.crm_object_type}:${sf.crm_name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(sf);
    }
  }
  return result;
}

const CANONICAL_TO_TABLE: Record<string, Record<string, string>> = {
  hubspot_v3: {
    Account: 'companies',
    Opportunity: 'deals',
  },
};

export function resolveObjectTypeToTable(
  crm_object_type: string,
  integrationType?: string,
): string {
  if (!integrationType) return crm_object_type;
  return (
    CANONICAL_TO_TABLE[integrationType]?.[crm_object_type] ?? crm_object_type
  );
}

export function mapSuggestedFieldsToIds(
  suggestedFields: SuggestedFieldType[],
  sources: Pick<Source, 'table' | 'fields'>[],
  integrationType?: string,
): number[] {
  const ids = new Set<number>();
  for (const sf of suggestedFields) {
    const table = resolveObjectTypeToTable(sf.crm_object_type, integrationType);
    const source = sources.find((s) => s.table === table);
    if (!source) continue;
    const field = source.fields.find((f) => f.column === sf.crm_name);
    if (field?.id && isValidSourceField(field)) ids.add(field.id);
  }
  return Array.from(ids);
}

export function getRequiredFieldIds(
  requiredColumns: Record<string, string[]>,
  sources: Pick<Source, 'table' | 'fields'>[],
): number[] {
  const ids = new Set<number>();
  for (const source of sources) {
    const required = requiredColumns[source.table];
    if (!required) continue;
    for (const field of source.fields) {
      if (
        field.id &&
        isValidSourceField(field) &&
        required.includes(field.column)
      )
        ids.add(field.id);
    }
  }
  return Array.from(ids);
}

export function isGoogleOrCsvSource(type: string) {
  return (
    type === GOOGLE_SHEETS_DATA_SOURCE_TYPE ||
    type === FILE_UPLOAD_DATA_SOURCE_TYPE
  );
}

export function getDataSourceLabel(
  type: DataSourceType,
  fallback: string,
): string {
  return DataSourceFriendlyName[type] ?? fallback;
}
